import React, { useState } from 'react';
import { BookOpen, Grid, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = '';  // Use relative URLs

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/${url}`;
};

/**
 * CoverImage - A stable, reusable component for rendering cover images across the site.
 * 
 * STABILITY RULES:
 * 1. Always uses the canonical field: cover_url
 * 2. Falls back to cover_image, thumbnail in that order
 * 3. On image load error, shows placeholder (never clears the URL)
 * 4. Placeholder varies by content type (comic vs novel)
 * 5. Resolves relative URLs to full URLs for production compatibility
 * 
 * This component ensures cover images are rendered consistently everywhere
 * and handles errors gracefully without data loss.
 */

// Get the cover URL from an item, checking multiple possible field names
export const getCoverUrl = (item) => {
  if (!item) return null;
  const rawUrl = item.cover_url || item.cover_image_url || item.cover_image || item.thumbnail_url || item.thumbnail || item.image_url || null;
  return resolveImageUrl(rawUrl);
};

// Check if a URL is valid (not empty, null, or placeholder)
export const isValidCoverUrl = (url) => {
  if (!url) return false;
  if (typeof url !== 'string') return false;
  if (url.trim() === '') return false;
  // Check for common invalid patterns
  if (url === 'null' || url === 'undefined') return false;
  return true;
};

const CoverImage = ({
  item,
  type = 'comic', // 'comic' | 'novel' | 'artwork'
  alt = 'Cover',
  className = '',
  aspectRatio = '2/3', // '2/3' for portrait, '16/9' for wide, '1/1' for square
  showPlaceholder = true,
  onError,
  onClick,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const coverUrl = getCoverUrl(item);
  const hasValidUrl = isValidCoverUrl(coverUrl) && !hasError;

  // Reset error and loading states when item or its cover changes
  React.useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [item?.id, item?.cover_url, item?.updated_at]);

  const handleError = (e) => {
    console.warn(`CoverImage: Failed to load image for "${item?.title || 'unknown'}"`, coverUrl);
    setHasError(true);
    setIsLoading(false);
    if (onError) onError(e);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  // Cache-bust URL using updated_at or a random timestamp
  const getCacheBustedUrl = (url) => {
    if (!url) return url;
    const timestamp = item?.updated_at ? new Date(item.updated_at).getTime() : Date.now();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${timestamp}`;
  };

  const displayUrl = getCacheBustedUrl(coverUrl);

  // Determine placeholder icon based on type
  const PlaceholderIcon = type === 'novel' ? BookOpen : type === 'artwork' ? ImageIcon : Grid;
  const placeholderBg = type === 'novel' 
    ? 'from-green-900/30 to-emerald-900/30' 
    : type === 'artwork'
    ? 'from-purple-900/30 to-pink-900/30'
    : 'from-gray-700 to-gray-800';
  const placeholderIconColor = type === 'novel' 
    ? 'text-green-700' 
    : type === 'artwork'
    ? 'text-purple-700'
    : 'text-gray-600';

  // Aspect ratio class mapping
  const aspectClasses = {
    '2/3': 'aspect-[2/3]',
    '16/9': 'aspect-video',
    '1/1': 'aspect-square',
    '3/4': 'aspect-[3/4]',
  };

  const aspectClass = aspectClasses[aspectRatio] || aspectClasses['2/3'];

  return (
    <div 
      className={`relative ${aspectClass} bg-gray-800 rounded-lg overflow-hidden ${className}`}
      onClick={onClick}
      data-testid={`cover-image-${item?.id || 'unknown'}`}
    >
      {hasValidUrl ? (
        <>
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="w-8 h-8 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin" />
            </div>
          )}
          {/* Actual image */}
          <img
            src={displayUrl}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
            onError={handleError}
            onLoad={handleLoad}
            loading="lazy"
            data-testid={`cover-image-img-${item?.id || 'unknown'}`}
          />
        </>
      ) : showPlaceholder ? (
        <div data-testid={`cover-image-fallback-${item?.id || 'unknown'}`} className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-br ${placeholderBg}`}>
          {item?.title ? (
            <span data-testid={`cover-image-initial-${item?.id || 'unknown'}`} className={`text-3xl font-bold ${placeholderIconColor} opacity-60 select-none`}>{item.title.charAt(0).toUpperCase()}</span>
          ) : (
            <PlaceholderIcon data-testid={`cover-image-icon-${item?.id || 'unknown'}`} className={`w-8 h-8 ${placeholderIconColor} opacity-60`} />
          )}
          {item?.title && (
            <span className={`text-xs mt-1 px-2 text-center ${placeholderIconColor} opacity-50 truncate max-w-[90%]`}>{item.title}</span>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CoverImage;
